import { useMemo } from 'react';
import { Text, Badge, Stack } from '@mantine/core';
import { format, parseISO, differenceInMonths } from 'date-fns';
import type { Livestock, LivestockType } from '@/types/livestock';

interface LivestockTimelineProps {
  livestock: Livestock[];
  onItemClick?: (livestock: Livestock) => void;
}

// Color config by livestock type
const typeColors: Record<LivestockType, string> = {
  fish: 'var(--mantine-color-blue-5)',
  coral: 'var(--mantine-color-teal-5)',
  invertebrate: 'var(--mantine-color-grape-5)',
  plant: 'var(--mantine-color-green-5)',
};

const typeBgColors: Record<LivestockType, string> = {
  fish: 'var(--mantine-color-blue-1)',
  coral: 'var(--mantine-color-teal-1)',
  invertebrate: 'var(--mantine-color-grape-1)',
  plant: 'var(--mantine-color-green-1)',
};

const typeBorderColors: Record<LivestockType, string> = {
  fish: 'var(--mantine-color-blue-3)',
  coral: 'var(--mantine-color-teal-3)',
  invertebrate: 'var(--mantine-color-grape-3)',
  plant: 'var(--mantine-color-green-3)',
};

const TYPE_LABELS: Record<LivestockType, string> = {
  fish: 'Fish',
  coral: 'Coral',
  invertebrate: 'Invertebrate',
  plant: 'Plant',
};

const GROUP_ORDER: LivestockType[] = ['fish', 'coral', 'invertebrate', 'plant'];

export function LivestockTimeline({ livestock, onItemClick }: LivestockTimelineProps) {
  const { months, startDate, totalMonths, grouped } = useMemo(() => {
    const allDates = livestock.flatMap((item) =>
      [item.acquisition_date, item.deceased_date]
        .filter(Boolean)
        .map((d) => parseISO(d!))
    );
    const now = new Date();
    allDates.push(now);

    const earliest = new Date(Math.min(...allDates.map((d) => d.getTime())));
    const start = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
    const total = differenceInMonths(now, start) + 2; // +2 for padding

    const monthLabels: { label: string; date: Date }[] = [];
    for (let i = 0; i < total; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      monthLabels.push({ label: format(d, 'MMM yy'), date: d });
    }

    const grouped = GROUP_ORDER.map((type) => ({
      type,
      label: TYPE_LABELS[type],
      items: livestock.filter((item) => item.livestock_type === type),
    }));

    return { months: monthLabels, startDate: start, totalMonths: total, grouped };
  }, [livestock]);

  const getBarStyle = (item: Livestock) => {
    const addedMonths = differenceInMonths(parseISO(item.acquisition_date), startDate);
    const endDate = item.deceased_date ? parseISO(item.deceased_date) : new Date();
    const durationMonths = differenceInMonths(endDate, parseISO(item.acquisition_date)) + 1;

    const left = `${(addedMonths / totalMonths) * 100}%`;
    const width = `${(Math.max(durationMonths, 1) / totalMonths) * 100}%`;
    return { left, width };
  };

  if (livestock.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        No livestock to display in timeline
      </Text>
    );
  }

  return (
    <Stack gap="xl">
      {grouped
        .filter((g) => g.items.length > 0)
        .map((group) => (
          <div key={group.type}>
            <Text size="sm" fw={600} mb="xs">
              {group.label}
            </Text>
            <div style={{ position: 'relative', overflowX: 'auto' }}>
              {/* Month header */}
              <div style={{
                display: 'flex',
                borderBottom: '1px solid var(--mantine-color-default-border)',
                marginBottom: 4,
                minWidth: 800,
              }}>
                {months.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      width: `${100 / totalMonths}%`,
                      fontSize: 10,
                      color: 'var(--mantine-color-dimmed)',
                      flexShrink: 0,
                    }}
                  >
                    {i % 3 === 0 ? m.label : ''}
                  </div>
                ))}
              </div>

              {/* Bars */}
              <div style={{ minWidth: 800 }}>
                {group.items.map((item) => {
                  const style = getBarStyle(item);
                  const isDeceased = item.status === 'deceased' || item.status === 'sold';
                  return (
                    <div
                      key={item.id}
                      style={{
                        position: 'relative',
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 4,
                      }}
                    >
                      {/* Grid lines */}
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        pointerEvents: 'none',
                      }}>
                        {months.map((_, i) => (
                          <div
                            key={i}
                            style={{
                              borderLeft: '1px solid var(--mantine-color-default-border)',
                              opacity: 0.2,
                              height: '100%',
                              flexShrink: 0,
                              width: `${100 / totalMonths}%`,
                            }}
                          />
                        ))}
                      </div>
                      {/* Bar */}
                      <div
                        style={{
                          position: 'absolute',
                          height: 24,
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: 8,
                          paddingRight: 8,
                          gap: 6,
                          fontSize: 12,
                          fontWeight: 500,
                          border: `1px solid ${typeBorderColors[item.livestock_type]}`,
                          backgroundColor: typeBgColors[item.livestock_type],
                          color: typeColors[item.livestock_type],
                          opacity: isDeceased ? 0.5 : 1,
                          left: style.left,
                          width: style.width,
                          minWidth: 80,
                          cursor: onItemClick ? 'pointer' : undefined,
                        }}
                        title={`${item.common_name} — ${format(parseISO(item.acquisition_date), 'MMM yyyy')} → ${
                          item.deceased_date
                            ? format(parseISO(item.deceased_date), 'MMM yyyy')
                            : 'Present'
                        }`}
                        onClick={onItemClick ? () => onItemClick(item) : undefined}
                      >
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {item.common_name}
                          {item.quantity > 1 && ` (×${item.quantity})`}
                        </span>
                        {isDeceased && (
                          <Badge
                            size="xs"
                            variant="outline"
                            color="red"
                            style={{ flexShrink: 0 }}
                          >
                            {item.status === 'sold' ? 'Sold' : 'Deceased'}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
    </Stack>
  );
}
